import React, {
  useState, useContext, useEffect, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FLAGS } from '../platform/config/flags';
import { isCustomerTypeAllowed, isSurfaceEnabled } from '../platform/config/surface';
import { emit, subscribe } from '../platform/events/lifecycle';
import { AssistantActionButton } from '../shared/fab/AssistantActionButton';
import { LazyAssistantDataProvider } from './dynamic-import';
import { LazyAssistantShell, loadAssistantShell } from '../shell/dynamic-import';
import { useDrawerController } from '../shell/useDrawerController';
import { DrawerControlContext } from '../shell/DrawerControlContext';
import { setLoggingEnabled } from '../shared/utils/logger';
import {
  getIsNewCompatiblePage, getIsLegacyCompatiblePage, waitForIdle,
} from '../shared/utils/assistant-utils';
// Renderer-free manifest import (see journeys/registry.js): safe for the eager main
// chunk — it does NOT pull the assistant runtime.
import { resolveJourneyId, DEFAULT_JOURNEY_ID } from '../journeys/registry';
import { useStickyElementOffset } from '../hooks/useStickyElementOffset';
import {
  HEADER_ENTRY, MAGIC_APRON_CONTAINER_ID, UPDATE_DRAWER_STATE,
} from '../shared/assistant-config';
import { buildAnalyticsQuestion } from '../shared/analytics';
import './app.scss';
import { JourneyProvider } from '../journeys';

// Eager bootstrap/composition root: surface gate, journey switch, host-event bus, FAB +
// deferred assistant tree. See .claude/context/app-bootstrap.md.

// Must stay ABOVE the lazy provider — a boundary never catches its own render errors.
// See .claude/context/app-bootstrap.md ("The error boundary sits above the lazy provider").
const logProviderError = ({ error, name }) => {
  // eslint-disable-next-line no-console
  console.error(`[magic-apron-assistant] "${name}" boundary caught an error:`, error);
};

export const App = ({ activateStyles }) => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  const { lesser: isMobile } = useBreakpoint('lg');
  // The journey the host page selected with the set-journey lifecycle event.
  const [journeyId, setJourneyId] = useState(DEFAULT_JOURNEY_ID);
  const offsetMargin = useStickyElementOffset();

  const fsDTEnabled = useConfigService(FLAGS.assistantDtEnabled);
  const fsMWEnabled = useConfigService(FLAGS.assistantMwEnabled);
  const fsAPPEnabled = useConfigService(FLAGS.assistantAppEnabled);
  const fsRenderOnB2B = useConfigService(FLAGS.assistantB2bEnabled) ?? false;
  // Opts INTO the consolidated journey. Legacy is the default, so anything that is not an
  // explicit true — unset, still resolving, or false — means legacy.
  const fsUnifiedEnabled = useConfigService(FLAGS.assistantUnifiedEnabled);
  const fsUnifiedAppEnabled = useConfigService(FLAGS.assistantUnifiedAppEnabled);
  const isValidCustomerType = isCustomerTypeAllowed(customer?.type, fsRenderOnB2B);
  // Gated on the rules flag ALONE — never on the journey flag (MAG4-815).
  // See .claude/context/page-classification.md ("Page eligibility is NOT coupled").
  const isExclusionRulesEnabled = useConfigService(FLAGS.exclusionRulesEnabled);
  const excludedPagesConfig = useConfigService(FLAGS.excludedPages);
  const isCompatiblePage = isExclusionRulesEnabled
    ? getIsNewCompatiblePage(excludedPagesConfig)
    : getIsLegacyCompatiblePage();

  useEffect(() => {
    if (isCompatiblePage && activateStyles) {
      activateStyles();
    }
  }, [isCompatiblePage]);

  const isLoggingEnabled = useConfigService('enable-maa-lc-console-logs');

  useEffect(() => {
    setLoggingEnabled(!!isLoggingEnabled);
  }, [isLoggingEnabled]);

  const {
    drawerState,
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  } = useDrawerController();

  const openDrawerRef = useRef(openDrawer);
  openDrawerRef.current = openDrawer;

  // A latch, not a mirror of drawerState — the data layer mounts on first
  // engagement and stays. See .claude/context/data-layer-sharing.md.
  const hasEverOpenedRef = useRef(false);
  if (drawerState !== 'CLOSED') hasEverOpenedRef.current = true;
  const withDataLayer = hasEverOpenedRef.current;

  // Read LIVE every render; do not latch it on the first defined value.
  // See .claude/context/app-bootstrap.md ("The journey switch").
  const isUnifiedAllowedHere = !isConsumerApp || fsUnifiedAppEnabled;
  const isUnified = Boolean(fsUnifiedEnabled) && isUnifiedAllowedHere;
  // The host page's requested journeyId is honored only once this surface is unified;
  // legacy has no journey concept of its own to route a hint into.
  const activeJourneyId = isUnified ? journeyId : 'legacy';

  // Memoized on behavior, not identity — see .claude/context/app-bootstrap.md.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const drawerControl = useMemo(() => ({ openDrawer: () => openDrawerRef.current() }), []);

  const handleMagicApronEvents = ({ eventName, output }) => {
    // Host-fired journey selection, normalized against the registry (unknown hint →
    // default). See .claude/context/journeys.md.
    if (eventName === 'set-journey') {
      setJourneyId(resolveJourneyId(output?.journeyId));
    }
    if (eventName === 'question-ask') {
      const {
        messageOrigin,
        pageType,
        entryLocationForAnalytics,
        userQuestion,
        sessionID,
        actionID
      } = output;
      markShouldAutoOpen();
      emit(HEADER_ENTRY.SUBMIT_EVENT, {
        sessionID,
        question: buildAnalyticsQuestion({
          entryLocation: entryLocationForAnalytics,
          pageType,
          messageOrigin,
          userQuestion,
        }),
        actionID,
      });
    }
  };

  useEffect(() => {
    const unsubscribeEvents = subscribe('magic-apron-assistant.*', handleMagicApronEvents);
    return () => {
      unsubscribeEvents();
    };
  }, []);

  const isHeaderEntryEligible = Boolean(
    isUnified
    && isCompatiblePage
    && isValidCustomerType
    && !isMobile
    && fsDTEnabled
  );

  // Above the `isCompatiblePage` early return on purpose: an excluded page still has to say
  // `available: false`.
  useEffect(() => {
    emit(
      HEADER_ENTRY.STATUS_EVENT,
      { available: isHeaderEntryEligible, drawerState },
      { channel: 'lifeCycle' },
    );
  }, [isHeaderEntryEligible, drawerState]);

  const handleHeaderRequestRef = useRef();
  handleHeaderRequestRef.current = (requestedDrawerState) => {
    if (requestedDrawerState === 'OPEN') handleOpen(true, HEADER_ENTRY.ORIGIN_HEADER);
    if (requestedDrawerState === 'CLOSED') handleClose(HEADER_ENTRY.ORIGIN_HEADER);
  };

  // Requests are absolute, so the bus replaying them — including on StrictMode's second
  // mount — is a no-op rather than a flip.
  useEffect(() => {
    const unsubscribeHeaderEntry = subscribe(
      UPDATE_DRAWER_STATE,
      ({ output } = {}) => handleHeaderRequestRef.current(output?.drawerState),
      { channel: 'lifeCycle' },
    );
    return () => {
      unsubscribeHeaderEntry();
    };
  }, []);

  const isSurfaceReady = isSurfaceEnabled({
    isMobile,
    isConsumerApp,
    dtEnabled: fsDTEnabled,
    mwEnabled: fsMWEnabled,
    appEnabled: fsAPPEnabled,
  // Parenthesize before adding an `&&` term here — it binds tighter than the `||` above.
  }) && isValidCustomerType && isCompatiblePage;

  // Prefetch the Shell chunk ahead of drawer-open. See .claude/context/shell-composition.md.
  useEffect(() => {
    if (!isSurfaceReady) return;
    waitForIdle(loadAssistantShell);
  }, [isSurfaceReady]);

  if (!isSurfaceReady) return null;

  // journeyId here is activeJourneyId — legacy overrides the host page's request when the
  // surface is not unified.
  const drawerProps = {
    drawerState,
    offsetMargin,
    journeyId: activeJourneyId,
    handleClose,
    handleDrawerStateChange,
  };

  return (
    <>
      {(drawerState === 'CLOSED') && (
        <ImpressionProvider
          data={{
            id: MAGIC_APRON_CONTAINER_ID,
            component: 'MagicApronAssistant',
            name: 'MagicApronLoader',
            type: 'content'
          }}
        >
          <AssistantActionButton
            offsetMargin={offsetMargin}
            onClick={() => handleOpen(true)}
            isUnifiedExperience={isUnified}
          />
        </ImpressionProvider>
      )}
      <ErrorBoundary name="magic-apron-assistant-dynamic-provider" onError={logProviderError}>
        {/* Wraps every journey; `legacy` has no consumer but the default is an inert
            no-op. See .claude/context/composition-topology.md. */}
        <DrawerControlContext.Provider value={drawerControl}>
          <LazyAssistantDataProvider journeyId={activeJourneyId} withDataLayer={withDataLayer}>
            {/* journeyId goes to BOTH halves; provider stays mounted while CLOSED (the
              warm path). See .claude/context/app-bootstrap.md. */}
            {(drawerState !== 'CLOSED') && (
              <JourneyProvider journeyId={activeJourneyId}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <LazyAssistantShell {...drawerProps} />
              </JourneyProvider>
            )}
          </LazyAssistantDataProvider>
        </DrawerControlContext.Provider>
      </ErrorBoundary>
    </>
  );
};

App.propTypes = {
  activateStyles: PropTypes.func,
};
